/**
 * Signing in.
 *
 * Each analyst has their own account, which is what makes the run trace and every decision say who
 * did it. A shared credential would make the audit log a list of things "the system" did.
 */

import { useState } from "react";
import { api, friendlyError, setSignedOut, setToken } from "../api/client";
import { Button, ErrorBanner } from "../components/ui";
import { BancorLogo } from "../components/BancorLogo";

export default function Login({
  onSignedIn,
  expired = false,
}: {
  onSignedIn: () => void;
  /** La sesión venció sola, en vez de que alguien haya cerrado sesión a propósito. */
  expired?: boolean;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<{ message: string; detail: string } | null>(null);
  const [checking, setChecking] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setChecking(true);
    setError(null);
    try {
      const { token } = await api.login(email.trim(), password);
      setToken(token);
      onSignedIn();
    } catch (e) {
      setError(friendlyError(e));
      setPassword("");
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="flex h-full">
      {/* The bank's side of the screen. A sign-in is the one place an internal tool gets to say
          whose system this is, and it costs nothing to read: no image, no gradient, one sentence
          that happens to be the rule the whole product is built on. */}
      <aside className="hidden w-[38%] max-w-md shrink-0 flex-col justify-between bg-rail p-10 text-rail-text lg:flex">
        <BancorLogo className="h-6 w-auto text-white" />
        <div>
          <p className="text-2xl leading-snug font-semibold text-white">
            El sistema redacta el borrador.
            <br />
            La decisión es del analista.
          </p>
          <p className="mt-4 max-w-xs text-sm text-white/60">
            Cada cifra del informe cita la página del documento de la que salió, y cada
            calificación, la política del banco que la produjo.
          </p>
        </div>
        <p className="text-xs text-white/40">Banco de Córdoba · Riesgo y Crédito Corporativo</p>
      </aside>

      <div className="flex flex-1 items-center justify-center bg-canvas px-6">
        <form onSubmit={submit} className="w-full max-w-sm" autoComplete="on">
          <div className="mb-8">
            <BancorLogo className="mb-4 h-6 w-auto text-rail lg:hidden" />
            <div className="text-xl font-semibold">Análisis de Crédito</div>
            <div className="text-[0.7rem] tracking-widest text-muted uppercase">
              Riesgo Corporativo
            </div>
          </div>

          {expired ?
            <p className="mb-5 rounded-lg border border-warn-line bg-warn-bg px-3 py-2 text-sm">
              Tu sesión venció. Ingresá de nuevo y volvés a donde estabas; lo que estuvieras
              escribiendo quedó guardado.
            </p>
          : null}

          <label className="block text-sm font-medium" htmlFor="email">
            Correo
          </label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="nombre@bancor.com.ar"
            className="mt-1.5 w-full rounded-lg border border-line bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none"
          />

          <label className="mt-4 block text-sm font-medium" htmlFor="password">
            Contraseña
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-line bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none"
          />

          {error ?
            <div className="mt-4">
              <ErrorBanner message={error.message} detail={error.detail} />
            </div>
          : null}

          <Button
            variant="primary"
            className="mt-5 w-full"
            disabled={!email.trim() || !password || checking}
          >
            {checking ? "Verificando…" : "Ingresar"}
          </Button>

          <p className="mt-4 text-xs text-muted">
            Si no tenés acceso, pedíselo a la administración del sistema.
          </p>

          {/* Development only, and compiled out of the build: with DEV_API_TOKEN set the proxy
              already signs every request, so this is how you get back in after using the sign-out
              button to look at this screen. */}
          {import.meta.env.DEV ?
            <button
              type="button"
              onClick={() => {
                setSignedOut(false);
                onSignedIn();
              }}
              className="mt-6 text-xs text-muted underline underline-offset-2 hover:text-ink"
            >
              Volver con la sesión del proxy de desarrollo
            </button>
          : null}
        </form>
      </div>
    </div>
  );
}
